/* eslint-disable @typescript-eslint/no-explicit-any */
import { api } from "../api";
import { clerkClient } from "@/App";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  Geofence,
  GeofenceInput,
  GeofenceUpdateInput,
  GeofenceGeometry,
  PolygonGeometry,
  GeofenceType,
  GeofencePolygonGeometry,
  GeofenceCircleGeometry,
  AlertSettings,
  AccessControl,
} from "./geofence-types";
import { circleGeometryToPolygon } from "../../../lib/geo-utils";

// Re-export types for convenience
export type {
  Geofence,
  GeofenceInput,
  GeofenceUpdateInput,
  GeofenceGeometry,
  GeofenceType,
  GeofencePolygonGeometry,
  GeofenceCircleGeometry,
  PolygonGeometry,
  AlertSettings,
  AccessControl,
};

/**
 * Helper to get the identifier for a geofence (slug if available, otherwise id)
 * This is useful when you need to reference a geofence and want to prefer slug over id
 */
export const getGeofenceIdentifier = (geofence: Geofence): string | number => {
  return geofence.slug && geofence.slug !== "" ? geofence.slug : geofence.id;
};

// Helper to convert frontend geometry to backend format
export const convertGeometryToBackend = (
  geometry: GeofenceGeometry
): PolygonGeometry => {
  if (geometry.type === "polygon") {
    return geometry.coordinates;
  } else {
    return circleGeometryToPolygon(geometry.center, geometry.radius);
  }
};

/**
 * Fetch all geofences accessible to the current user
 */
export const fetchGeofences = async (): Promise<Geofence[]> => {
  const authToken = await clerkClient.session?.getToken();
  if (!authToken) {
    throw new Error("Authentication required");
  }
  const res = await api.get<Geofence[]>("/geofences", {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return res.data;
};

/**
 * Fetch a single geofence by slug or id
 * @param slugOrId - Either a slug (string) or numeric id (number)
 *                    If slug is an empty string, it will be used as-is (may cause API error)
 *                    Prefer using numeric id when slug is empty
 */
export const fetchGeofenceById = async (slugOrId: string | number): Promise<Geofence> => {
  const authToken = await clerkClient.session?.getToken();
  if (!authToken) {
    throw new Error("Authentication required");
  }
  // Use the identifier directly - API should handle both slug and numeric id
  const res = await api.get<Geofence>(`/geofences/${slugOrId}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return res.data;
};

/**
 * Create a new geofence
 */
export const createGeofence = async (
  input: GeofenceInput
): Promise<Geofence> => {
  const authToken = await clerkClient.session?.getToken();
  if (!authToken) {
    throw new Error("Authentication required");
  }

  // Convert geometry to backend format
  const backendGeometry = convertGeometryToBackend(input.geometry);

  const payload = {
    name: input.name,
    description: input.description,
    organisation_id: input.organisation_id,
    type: input.type,
    geometry: backendGeometry,
    alert_settings: input.alert_settings,
    access_control: input.access_control,
    is_active: input.is_active ?? true,
    min_altitude_agl_ft: input.min_altitude_agl_ft,
    max_altitude_agl_ft: input.max_altitude_agl_ft,
    effective_start_time: input.effective_start_time,
    effective_end_time: input.effective_end_time,
  };

  const res = await api.post<Geofence>("/geofences", payload, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return res.data;
};

/**
 * Update an existing geofence
 * Can use either slug or id (if slug is empty)
 */
export const updateGeofence = async ({
  slug,
  id,
  geofenceUpdateInput,
}: {
  slug?: string;
  id?: number;
  geofenceUpdateInput: GeofenceUpdateInput;
}): Promise<Geofence> => {
  const authToken = await clerkClient.session?.getToken();
  if (!authToken) {
    throw new Error("Authentication required");
  }

  // Use slug if available and not empty, otherwise use numeric id
  const identifier = (slug && slug !== "") ? slug : id;
  if (identifier === undefined || identifier === null) {
    throw new Error("Either slug or id must be provided");
  }

  const payload: any = {
    name: geofenceUpdateInput.name,
    description: geofenceUpdateInput.description,
    organisation_id: geofenceUpdateInput.organisation_id,
    type: geofenceUpdateInput.type,
    alert_settings: geofenceUpdateInput.alert_settings,
    access_control: geofenceUpdateInput.access_control,
    is_active: geofenceUpdateInput.is_active,
    min_altitude_agl_ft: geofenceUpdateInput.min_altitude_agl_ft,
    max_altitude_agl_ft: geofenceUpdateInput.max_altitude_agl_ft,
    effective_start_time: geofenceUpdateInput.effective_start_time,
    effective_end_time: geofenceUpdateInput.effective_end_time,
  };

  // Convert geometry if provided
  if (geofenceUpdateInput.geometry) {
    payload.geometry = convertGeometryToBackend(geofenceUpdateInput.geometry);
  }

  // Remove undefined values
  Object.keys(payload).forEach(
    (key) => payload[key] === undefined && delete payload[key]
  );

  const res = await api.patch<Geofence>(`/geofences/${identifier}`, payload, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
  return res.data;
};

/**
 * Delete a geofence (soft delete)
 * @param slugOrId - Either a slug (string) or numeric id (number)
 */
export const deleteGeofence = async (slugOrId: string | number): Promise<void> => {
  const authToken = await clerkClient.session?.getToken();
  if (!authToken) {
    throw new Error("Authentication required");
  }
  await api.delete(`/geofences/${slugOrId}`, {
    headers: { Authorization: `Bearer ${authToken}` },
  });
};

// React Query hooks

export const useGeofencesQueryV1 = () => {
  return useQuery({
    queryKey: ["geofences"],
    queryFn: fetchGeofences,
  });
};

export const useGeofenceQueryV1 = (slugOrId: string | number) => {
  return useQuery({
    queryKey: ["geofence", slugOrId],
    queryFn: () => fetchGeofenceById(slugOrId),
    enabled: !!slugOrId || slugOrId === 0,
  });
};

export const useCreateGeofenceMutationV1 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createGeofence,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["geofences"] });
    },
  });
};

export const useUpdateGeofenceMutationV1 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateGeofence,
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["geofences"] });
      // Invalidate by both slug and id if available
      if (variables.slug) {
        queryClient.invalidateQueries({ queryKey: ["geofence", variables.slug] });
      }
      if (variables.id) {
        queryClient.invalidateQueries({ queryKey: ["geofence", variables.id] });
      }
    },
  });
};

export const useDeleteGeofenceMutationV1 = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteGeofence,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["geofences"] });
    },
  });
};
